import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { VtjOteTodistusType } from 'todistukset-common/src/vtj/CertificateTypes.enum'
import AdditionalSelectionsStep from 'todistukset-ui/src/vtj/components/order-wizard/AdditionalSelectionsStep.react'
import BasicInformationStep from 'todistukset-ui/src/vtj/components/order-wizard/BasicInformationStep.react'
import DeliveryMethodStep from 'todistukset-ui/src/vtj/components/order-wizard/DeliveryMethodStep.react'
import InstructionsStep from 'todistukset-ui/src/vtj/components/order-wizard/InstructionsStep/InstructionsStep.react'
import OrderCompleteStep from 'todistukset-ui/src/vtj/components/order-wizard/OrderCompleteStep.react'
import OrderSummaryStep from 'todistukset-ui/src/vtj/components/order-wizard/OrderSummaryStep/OrderSummary.react'
import PersonalInformationStep from 'todistukset-ui/src/vtj/components/order-wizard/PersonalInformationStep.react'
import UsageRegionStep from 'todistukset-ui/src/vtj/components/order-wizard/UsageRegionStep.react'
import {
  OnClickParams,
  Step,
  StepButtons,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  validateBasicInformationFields,
  validateContactInfoFields,
  validateDeliveryMethodFields,
  validateLanguageFields,
  validateOrdererExtraIdentificationFields,
  validateUsageRegionFields,
} from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import getFormActions from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'
import { refreshSession } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import StepRoutes from 'todistukset-ui/src/vtj/todistukset-step-routes.enum'
import { Serialized } from 'common/src/vtj/types/util.type'
import {
  Payment,
  TodistusOrder,
} from 'todistukset-common/src/vtj/types/todistus-order.type'

// eslint-disable-next-line @typescript-eslint/no-empty-function
function doNothing(): void {}

const refreshOnClick = ({ onSuccess }: OnClickParams): void => {
  refreshSession()
  onSuccess()
}

const getRefreshOnClick = () => refreshOnClick

export function getVtjOteTodistusSteps(
  todistusType: VtjOteTodistusType,
  isEidasUser: boolean
): Step[] {
  type UnindexedStep = Omit<Step, 'index' | 'getPath'>
  const steps: (UnindexedStep | false)[] = [
    {
      name: StepRoutes.INSTRUCTIONS,
      component: InstructionsStep,
      validations: [],
      onPrevClick: doNothing,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.NEXT,
    },
    isEidasUser && {
      name: StepRoutes.EIDAS_PERSONAL_INFORMATION,
      component: PersonalInformationStep,
      validations: [validateOrdererExtraIdentificationFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
    },
    {
      name: StepRoutes.BASIC_INFORMATION,
      component: BasicInformationStep,
      validations: [validateBasicInformationFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
    },
    {
      name: StepRoutes.USAGE_REGION,
      component: UsageRegionStep,
      validations: [validateUsageRegionFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
    },
    {
      name: StepRoutes.ADDITIONAL_SELECTIONS,
      component: AdditionalSelectionsStep,
      validations: [validateLanguageFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
    },
    {
      name: StepRoutes.DELIVERY_METHOD,
      component: DeliveryMethodStep,
      validations: [validateDeliveryMethodFields, validateContactInfoFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
    },
    {
      name: StepRoutes.SUMMARY,
      component: OrderSummaryStep,
      validations: [],
      onPrevClick: refreshOnClick,
      useOnNextClick: () => () => {
        return getFormActions(todistusType).submitForm()
      },
      buttons: StepButtons.PREV_AND_ORDER,
    },
    {
      name: StepRoutes.ORDER_COMPLETE,
      component: OrderCompleteStep,
      validations: [],
      onPrevClick: doNothing,
      useOnNextClick: () => doNothing,
      buttons: StepButtons.NONE,
    },
  ]
  return steps
    .filter((step): step is UnindexedStep => !!step)
    .map((step, index) => {
      return {
        ...step,
        index,
        getPath: (lang = LanguageCode.FI) =>
          `/${lang}/${todistusType}/${step.name}`,
      }
    })
}

export const getOrderCompleteUrl = (
  todistusOrder: Serialized<TodistusOrder & { payment: Payment }>
): string => {
  const lang = todistusOrder.payment.ordererUiLanguage
  const baseUrl = `/${todistusOrder.form.todistusType}/${StepRoutes.ORDER_COMPLETE}/${todistusOrder.payment.paymentId}`
  const orderCompleteUrl = lang ? `/${lang}${baseUrl}` : baseUrl
  return orderCompleteUrl
}
